import { Helmet } from "react-helmet-async";
import { motion } from "motion/react";
import { Button } from "../ui/button";
// JS components, add TS ignore to satisfy type checker
// @ts-ignore
import { Navbar } from "./Navbar";
// @ts-ignore
import { Footer } from "./Footer";
import Logo from "../dashboard/Logo";

interface LandingPageProps {
  onStartTrial?: () => void;
  onSignIn?: () => void;
  onRegister?: () => void;
}

export function LandingPage({ onStartTrial, onSignIn, onRegister }: LandingPageProps) {
  return (
    <div className="relative h-screen flex flex-col bg-gradient-to-br from-background via-muted/20 to-background overflow-hidden">
      <Helmet>
        <title>{`BandarmoloNY`}</title>
        <meta
          name="description"
          content="Bandarmology platform for active traders"
        />
        <link rel="canonical" href="https://bandarmolony.com/" />
        <meta
          property="og:title"
          content="BandarmoloNY"
        />
        <meta
          property="og:description"
          content="Bandarmology platform for active traders"
        />
        <meta property="og:url" content="https://bandarmolony.com/" />
        <meta property="og:image" content="https://bandarmolony.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="BandarmoloNY \u2014 Intelligent Market Analysis"
        />
        <meta
          name="twitter:description"
          content="Bandarmology platform for active traders"
        />
        <meta name="twitter:image" content="https://bandarmolony.com/og-image.jpg" />
      </Helmet>

      {/* Background decorative elements removed as requested */}
      <div className="flex-shrink-0">
        <Navbar
          {...(onSignIn && { onSignIn })}
          {...(onRegister && { onRegister })}
        />
      </div>

      <main className="relative z-10 flex flex-1 flex-col items-center px-6 pt-24 pb-6 text-center w-full max-w-7xl mx-auto min-h-0">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex-shrink-0 flex flex-col items-center"
        >
          <div className="mb-1 flex items-center justify-center gap-6">
            <Logo
              className="h-14 md:h-20"
              badgeClassName="h-14 w-16 md:h-20 md:w-24"
              textClassName="text-3xl md:text-6xl font-bold text-foreground tracking-tight drop-shadow-2xl"
              showText={true}
            />
          </div>

          <motion.p
            className="mb-8 max-w-2xl text-sm md:text-lg font-light leading-relaxed tracking-wide text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            Bandarmology platform for active traders
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            <Button
              size="lg"
              onClick={onStartTrial}
              className="transform rounded-xl border-0 bg-red-800 px-8 py-5 md:py-6 text-sm md:text-base font-semibold text-white shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:bg-red-900 hover:shadow-red-900/40"
            >
              Start Free Trial
            </Button>
          </motion.div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="flex-1 w-full min-h-0 mt-8 relative flex justify-center items-start px-2 md:px-8"
        >
          <div className="relative max-h-full max-w-5xl flex flex-col items-center justify-start min-h-0 mb-6">
            {/* Subtle red glow behind the laptop */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-red-500/40 blur-[100px] rounded-full pointer-events-none z-0"></div>

            {/* Thin Screen Frame */}
            <div className="relative z-10 rounded-t-lg md:rounded-t-2xl rounded-b-sm border-[3px] md:border-[5px] border-gray-800/90 bg-gray-900 p-0.5 shadow-[0_0_50px_rgba(239,68,68,0.25)] flex min-h-0 max-h-full aspect-video max-w-full">
              {/* Optional tiny camera dot for subtle laptop feel */}
              <div className="absolute top-0 md:-top-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-gray-700 z-20"></div>
              
              {/* Video container */}
              <div className="relative w-full h-full rounded-sm overflow-hidden bg-black flex justify-center items-center">
                {/* The poster (first frame, ~25 KB) shows until the video plays, and its size sizes the
                    laptop meanwhile: without it the frame shrinks to the 300x150 default video size */}
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  poster="/assets/images/hero_poster.webp"
                  className="w-full h-full object-cover"
                >
                  <source src="/assets/videos/hero_background.mp4" type="video/mp4" />
                </video>
              </div>

              {/* Laptop Base */}
              <div className="absolute top-[100%] left-1/2 -translate-x-1/2 w-[105%] md:w-[108%] h-3 md:h-5 bg-gradient-to-b from-gray-600 to-gray-900 rounded-b-lg md:rounded-b-2xl shadow-2xl flex justify-center items-start border-t border-gray-500 z-30">
                 {/* Trackpad notch */}
                 <div className="w-1/5 md:w-1/6 h-1 md:h-1.5 bg-gray-800 rounded-b-md opacity-60"></div>
              </div>
            </div>
          </div>
        </motion.div>
      </main>

      {/* SEO Backlinks Hidden Section */}
      <div className="sr-only">
        <h1>BandarmoloNY - Intelligent Market Analysis Platform</h1>
        <nav>
          <ul>
            <li><a href="https://bandarmolony.com/features" rel="nofollow noopener noreferrer">Trading Platform Features</a></li>
            <li><a href="https://bandarmolony.com/pricing" rel="nofollow noopener noreferrer">Subscription Pricing Plans</a></li>
            <li><a href="https://bandarmolony.com/contact" rel="nofollow noopener noreferrer">Contact Trading Platform Team</a></li>
          </ul>
        </nav>
        <p>BandarmoloNY is an advanced trading analysis platform for Indonesian stock market. Bandarmology platform for active traders</p>
        <p>Professional stock market analysis tools including market rotation, broker activity tracking, technical analysis, and more. Get started with a free trial today.</p>
      </div>

      <div className="flex-shrink-0">
        <Footer />
      </div>
    </div>
  );
}
